export type BackgroundJob = () => Promise<unknown>;

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void) => number;
  cancelIdleCallback?: (id: number) => void;
  navigator: Navigator & { connection?: { saveData?: boolean; effectiveType?: string } };
};

/** Only pending callbacks are cancelled. An import already running may finish. */
export function createHomepageBackgroundQueue(host: IdleWindow) {
  let jobs: BackgroundJob[] = [];
  let paused = true;
  let disposed = false;
  let running = false;
  let cancelPending: (() => void) | undefined;

  const clearPending = () => {
    cancelPending?.();
    cancelPending = undefined;
  };
  const schedule = () => {
    if (disposed || paused || running || cancelPending || !jobs.length) return;
    if (host.document.visibilityState === "hidden") return;
    const connection = host.navigator.connection;
    if (connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType || "")) return;

    // Yield a paint before asking for idle time, including between jobs.
    const frame = host.requestAnimationFrame(() => {
      const run = () => {
        cancelPending = undefined;
        if (disposed || paused || host.document.visibilityState === "hidden") return;
        const job = jobs.shift();
        if (!job) return;
        running = true;
        void Promise.resolve()
          .then(job)
          .catch(() => {
            // Speculative failures remain retryable through the feature's own UI.
          })
          .finally(() => {
            running = false;
            schedule();
          });
      };
      if (host.requestIdleCallback && host.cancelIdleCallback) {
        const idle = host.requestIdleCallback(run);
        cancelPending = () => host.cancelIdleCallback?.(idle);
      } else {
        const timer = host.setTimeout(run, 100);
        cancelPending = () => host.clearTimeout(timer);
      }
    });
    cancelPending = () => host.cancelAnimationFrame(frame);
  };
  const visibilityChanged = () => {
    clearPending();
    schedule();
  };
  host.document.addEventListener("visibilitychange", visibilityChanged);
  return {
    replace(next: BackgroundJob[]) {
      clearPending();
      jobs = [...next];
      schedule();
    },
    pause(value: boolean) {
      paused = value;
      clearPending();
      schedule();
    },
    dispose() {
      disposed = true;
      clearPending();
      jobs = [];
      host.document.removeEventListener("visibilitychange", visibilityChanged);
    },
  };
}
